import { Controller } from "@hotwired/stimulus"
import { highlightCodeWithin } from "../lib/syntax_highlighting"
import { fieldLabel } from "../lib/field_label"
import { displayFor, leafKey } from "../lib/value_format"

// Mirrors PaperworksHelper::EXTRACTED_DATA_SUMMARY_* so client-built summary
// cells/headers match server-rendered ones byte for byte.
const SUMMARY_COLUMN_LIMIT = 3
const SUMMARY_CELL_CLASS = "py-2 px-3 truncate"
const SUMMARY_CELL_NUMERIC_CLASS = `${SUMMARY_CELL_CLASS} text-right tabular-nums`
const SUMMARY_HEADER_CLASS = "py-1.5 px-3 truncate"
const SUMMARY_HEADER_NUMERIC_CLASS = `${SUMMARY_HEADER_CLASS} text-right`
const SUMMARY_NUMERIC_WIDTH_PX = 84
const AUTO_EXPAND_LIMIT = 3
const INDEX_KEY_PATTERN = /^(?:index|idx|position|ordinal)$|^(?:line|row|page|seq|sequence)(?:_?item)?_?(?:number|no|num|index|idx)?$/
const LABEL_KEY_PATTERN = /desc|name|title|label|summary|product|service/
// Mirrors PaperworksHelper::EXTRACTED_DATA_FIELD_LABEL_KEY_PATTERN — a key that
// names a neighbouring field, not a column of its own.
const FIELD_LABEL_KEY_PATTERN = /(^|_)label$/
// Mirrors PaperworksHelper::EXTRACTED_DATA_DATE_KEY_PATTERN.
const DATE_KEY_PATTERN = /(^|_)date($|_)/
const TOTAL_KEY_PATTERN = /total|amount|extended|\bnet\b/

// Connects to data-controller="extracted-data-editor"
export default class extends Controller {
  static targets = [
    "formView", "jsonView", "jsonCode", "expandBtn", "formTab", "jsonTab", "copyBtn",
    "lazyRest", "fieldRowTemplate", "arrayItemTemplate", "sectionTemplate",
    "arrayRowTemplate", "arrayDetailTemplate", "arrayTableTemplate",
    "find", "filter", "filterNone"
  ]
  static values = {
    url: String,
    deleteUrl: String,
    json: String,
    reviewMode: { type: Boolean, default: false },
    highlightable: { type: Boolean, default: true }
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (this.hasFormViewTarget) this._formatValues(this.formViewTarget)
    // Capture phase: inside the open viewer, "/" belongs to this field and
    // must not also reach the page's search hotkey or command palette.
    this._boundSlash = this._focusFilterOnSlash.bind(this)
    document.addEventListener("keydown", this._boundSlash, true)
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundSlash, true)
  }

  // ---- Find a field ----------------------------------------------------------
  // Narrows the form to the fields whose label, value, description or path
  // holds every typed word. A section whose title matches keeps all its
  // fields. Sections with a match open; the rest step aside until the filter
  // is cleared, when each section goes back to how it was. Line items that
  // were never opened match on their summary row only.

  filterFields() {
    if (!this.hasFilterTarget) return
    const words = this.filterTarget.value.toLowerCase().split(/\s+/).filter(Boolean)
    const root = this.formViewTarget
    const active = words.length > 0
    const matches = text => words.every(word => text.includes(word))
    root.classList.toggle("is-filtering", active)

    const details = Array.from(root.querySelectorAll("details.extracted-data-details"))
    if (active) details.forEach(d => { if (d.dataset.xdWasOpen === undefined) d.dataset.xdWasOpen = d.open ? "1" : "0" })

    // Fields under a section whose own title matches all stay in view.
    const titleHits = active
      ? details.filter(d => matches(d.querySelector(":scope > summary")?.textContent.toLowerCase() || ""))
      : []

    let shown = 0
    root.querySelectorAll("tr[data-field-path], tr[data-role='item-row']").forEach(row => {
      const text = `${row.textContent} ${row.dataset.fieldPath || ""} ${row.dataset.rawValue || ""}`.toLowerCase()
      const hit = !active || matches(text) || titleHits.some(d => d.contains(row))
      row.classList.toggle("xd-filtered-out", !hit)
      if (hit && active) shown += 1
    })

    // Innermost first, so a parent sees its children's result.
    details.reverse().forEach(d => {
      const wrapper = d.closest("tr")
      if (!active) {
        wrapper?.classList.remove("xd-filtered-out")
        if (d.dataset.xdWasOpen !== undefined) {
          d.open = d.dataset.xdWasOpen === "1"
          delete d.dataset.xdWasOpen
        }
        return
      }
      const any = d.querySelector("tr[data-field-path]:not(.xd-filtered-out), tr[data-role='item-row']:not(.xd-filtered-out)")
      d.open = Boolean(any)
      wrapper?.classList.toggle("xd-filtered-out", !any)
    })

    root.querySelectorAll("tr[data-role='item-detail']").forEach(detail => {
      const any = detail.querySelector("tr[data-field-path]:not(.xd-filtered-out)")
      detail.classList.toggle("xd-filtered-out", active && !any)
    })

    if (this.hasFilterNoneTarget) this.filterNoneTarget.classList.toggle("hidden", !active || shown > 0)
  }

  clearFilter(event) {
    if (!this.hasFilterTarget || !this.filterTarget.value) return
    // Escape clears the filter first; a second Escape closes the viewer.
    event?.preventDefault()
    event?.stopPropagation()
    this.filterTarget.value = ""
    this.filterFields()
  }

  // Only inside the dialog on top: on a page, "/" keeps its page shortcut.
  _focusFilterOnSlash(event) {
    if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return
    if (!this.hasFilterTarget || this.reviewModeActive() || this.formViewTarget.classList.contains("hidden")) return
    const dialog = this.element.closest("dialog[open]")
    const openDialogs = document.querySelectorAll("dialog[open]")
    if (!dialog || openDialogs[openDialogs.length - 1] !== dialog) return
    const target = event.target
    if (target?.closest?.("input, textarea, select, [contenteditable='true']")) return
    if (!this.filterTarget.offsetParent) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.filterTarget.focus()
    this.filterTarget.select()
  }

  enterReviewMode() {
    this.clearFilter()
    this.reviewModeValue = true
    this.showForm()
    this.setJsonControlsEnabled(false)
  }

  exitReviewMode() {
    this.reviewModeValue = false
    this.setJsonControlsEnabled(true)
    this.element.querySelectorAll(".pdf-review-row--editing").forEach(row => this._closeEditor(row))
  }

  showForm(event) {
    event?.preventDefault()
    this.formViewTarget.classList.remove("hidden")
    this.jsonViewTarget.classList.add("hidden")
    this.formTabTarget.classList.add("tab-active")
    this.jsonTabTarget.classList.remove("tab-active")
    if (this.hasFindTarget) this.findTarget.classList.remove("hidden")
  }

  showJson(event) {
    event?.preventDefault()
    if (this.reviewModeActive()) {
      this.showForm()
      this.flashReviewModeNotice()
      return
    }

    this.fillJson()
    this.highlightJson()
    this.formViewTarget.classList.add("hidden")
    this.jsonViewTarget.classList.remove("hidden")
    this.jsonTabTarget.classList.add("tab-active")
    this.formTabTarget.classList.remove("tab-active")
    if (this.hasFindTarget) this.findTarget.classList.add("hidden")
  }

  // The JSON tab's text is not server-rendered — the same document already
  // ships in the editor's json value, and a second HTML-escaped copy doubled
  // the response on a document with many line items. Write it on first open.
  fillJson() {
    if (!this.hasJsonCodeTarget) return
    const code = this.jsonCodeTarget
    if (!code.dataset.jsonPending) return
    delete code.dataset.jsonPending
    code.textContent = this.jsonValue
  }

  highlightJson() {
    if (!this.hasJsonCodeTarget || this.jsonCodeTarget.dataset.highlighted === "yes") return
    if (!this.highlightableValue) return
    this.jsonCodeTarget.classList.remove("nohighlight")
    this.jsonCodeTarget.classList.add("language-json")
    highlightCodeWithin(this.jsonCodeTarget)
  }

  async copyJson() {
    try {
      await navigator.clipboard.writeText(this.jsonValue)
      const btn = this.copyBtnTarget
      const original = btn.innerHTML
      btn.innerHTML = `<i class="ti ti-check text-xs"></i>`
      setTimeout(() => { btn.innerHTML = original }, 1500)
    } catch (e) {
      console.error("Copy failed:", e)
    }
  }

  toggleAll() {
    const open = !this._fullyExpanded(this.formViewTarget)
    this._setExpanded(this.formViewTarget, open)
    this._setExpandIcon(this.expandBtnTarget, open, "all sections")
  }

  // Same expand/collapse, scoped to the section whose header was clicked.
  toggleSectionAll(event) {
    // The button sits inside a <summary>; without this the browser also runs
    // the native <details> toggle after the handlers, undoing the state below.
    event.preventDefault()
    event.stopPropagation()

    const details = event.currentTarget.closest("details")
    if (!details) return

    const open = !(details.open && this._fullyExpanded(details))
    details.open = open
    this._setExpanded(details, open)
    this._setExpandIcon(event.currentTarget, open, "all in this section")
  }

  _fullyExpanded(root) {
    return Array.from(root.querySelectorAll("details")).every(d => d.open) &&
      Array.from(root.querySelectorAll("[data-role='item-detail']")).every(row => !row.classList.contains("hidden"))
  }

  _setExpanded(root, open) {
    // Materialize first: lazily deferred items have no DOM to toggle yet.
    if (open) this._materializeWithin(root)

    root.querySelectorAll("details").forEach(d => { d.open = open })
    root.querySelectorAll("[data-role='item-detail']").forEach(row => {
      if (open) this._hydrateDetail(row)
      row.classList.toggle("hidden", !open)
      row.previousElementSibling?.setAttribute("aria-expanded", String(open))
    })
  }

  _setExpandIcon(button, open, scope) {
    const icon = button.querySelector("i")
    if (icon) icon.className = `ti ti-arrows-${open ? "minimize" : "maximize"} text-xs`
    button.title = `${open ? "Collapse" : "Expand"} ${scope}`
  }

  toggleSection(event) {
    const interactiveTarget = event.target.closest("button, a, input, textarea, select, label")
    if (interactiveTarget && interactiveTarget !== event.currentTarget) return

    const summary = event.currentTarget
    const details = summary.closest("details")
    if (!details) return

    event.preventDefault()
    details.open = !details.open
  }

  // Summary rows in array tables expand their paired detail row; clicking a
  // value cell also opens that field's inline editor inside the detail.
  toggleItemRow(event) {
    if (event.target.closest("button, a, input, textarea, select, label")) return

    const row = event.currentTarget
    const detail = this._detailRowFor(row)
    if (!detail) return

    // Its fields may not exist yet — build them before anything looks inside.
    this._hydrateDetail(detail)

    const cell = event.target.closest("[data-summary-path]")
    if (cell && !this.reviewModeActive()) {
      const fieldRow = detail.querySelector(`[data-field-path="${CSS.escape(cell.dataset.summaryPath)}"]`)
      if (fieldRow && fieldRow.querySelector("[data-role='editor']")) {
        this._setItemRowExpanded(row, detail, true)
        this._openEditorForRow(fieldRow)
        return
      }
    }

    this._setItemRowExpanded(row, detail, detail.classList.contains("hidden"))
  }

  // Makes one field's row visible (a task's field link, pdf-review landOnField):
  // builds rows a large array has not drawn yet, opens the sections around it,
  // and opens its array item's detail row. Returns the row, or null.
  revealField(fieldPath) {
    const selector = `[data-field-path="${CSS.escape(fieldPath)}"]`
    let row = this.element.querySelector(selector)
    if (!row) {
      this.materializeAll()
      row = this.element.querySelector(selector)
    }
    if (!row) return null

    const detail = row.closest("[data-role='item-detail']")
    const summary = detail?.previousElementSibling
    if (detail && summary) this._setItemRowExpanded(summary, detail, true)
    for (let details = row.closest("details"); details; details = details.parentElement?.closest("details")) {
      details.open = true
    }
    return row
  }

  _detailRowFor(row) {
    const next = row.nextElementSibling
    return next?.matches("[data-role='item-detail']") ? next : null
  }

  _setItemRowExpanded(row, detail, open) {
    if (open) this._hydrateDetail(detail)
    detail.classList.toggle("hidden", !open)
    row.setAttribute("aria-expanded", String(open))
  }

  // A collapsed detail row ships empty (see _extracted_data_array_detail) —
  // several KB of markup per item that nobody sees until the row is opened.
  // Build its fields here, from the same JSON the lazy rows are built from.
  _hydrateDetail(detail) {
    if (!detail?.dataset.detailPending) return
    delete detail.dataset.detailPending

    const itemPath = detail.previousElementSibling?.dataset.itemPath
    const td = detail.querySelector("td")
    if (!itemPath || !td) return

    const item = this._valueAtPath(itemPath)
    if (!this._isPlainObject(item)) return

    const table = detail.closest("table[data-role='array-table']")
    let itemSchema = {}
    try { itemSchema = JSON.parse(table?.dataset.itemSchema || "{}") } catch { /* schema optional */ }
    const depth = parseInt(table?.dataset.depth, 10) || 0

    td.appendChild(this._buildRowsTable(item, itemPath, depth + 1, itemSchema))
  }

  edit(event) {
    if (this.reviewModeActive()) return

    const row = event.currentTarget
    this._openEditorForRow(row)
  }

  // +value+ types a suggested value into the editor (the one the page shows), so
  // Enter saves it.
  openFieldEditorForReview(fieldPath, { value } = {}) {
    if (!fieldPath) return false

    this.enterReviewMode()
    const row = this.element.querySelector(`[data-field-path="${CSS.escape(fieldPath)}"]`)
    if (!row) return false

    this._revealFieldRow(row)
    this._openEditorForRow(row, { fromReview: true })
    if (value != null) {
      const input = [...row.querySelectorAll("[data-role='editor'] input, [data-role='editor'] textarea")].find(el => !el.classList.contains("hidden"))
      if (input) {
        input.value = value
        input.focus()
        input.select()
      }
    }
    row.dispatchEvent(new CustomEvent("extracted-data-editor:field-editing", {
      bubbles: true,
      detail: { fieldPath }
    }))
    return true
  }

  _openEditorForRow(row, { fromReview = false } = {}) {
    if (this.reviewModeActive() && !fromReview) return

    // Don't re-open if already editing
    const editor = row.querySelector("[data-role='editor']")
    if (editor && !editor.classList.contains("hidden")) return

    const display = row.querySelector("[data-role='display']")
    if (!display || !editor) return

    // The stored value, not the formatted one ("1180.0", not "1,180.00"), and
    // nothing for an empty field.
    const empty = display.querySelector("[data-role='empty-placeholder']")
    const value = empty ? "" : (row.dataset.rawValue ?? display.textContent.trim())

    // Detect if text overflows the display span or contains newlines
    const textarea = editor.querySelector("textarea")
    const input = editor.querySelector("input")
    // The display wraps to three lines, so a value that wraps (or is cut at
    // three lines) gets the textarea too.
    const isOverflowing = display.scrollWidth > display.clientWidth ||
      display.scrollHeight > display.clientHeight + 1 ||
      display.getClientRects().length > 0 && display.getBoundingClientRect().height > parseFloat(getComputedStyle(display).lineHeight) * 1.5
    const hasNewlines = value.includes("\n")
    const useTextarea = textarea && (isOverflowing || hasNewlines)

    let activeInput
    if (useTextarea) {
      textarea.classList.remove("hidden")
      if (input) input.classList.add("hidden")
      editor.classList.replace("items-center", "items-start")
      activeInput = textarea
    } else {
      if (textarea) textarea.classList.add("hidden")
      if (input) input.classList.remove("hidden")
      activeInput = input
    }

    if (activeInput) {
      activeInput.value = value
      activeInput.dataset.originalValue = value
    }

    display.classList.add("hidden")
    editor.classList.remove("hidden")
    row.classList.toggle("pdf-review-row--editing", fromReview)

    // A light wash on the row; the description shows in full under the label
    // while the field is open (extracted_data.css).
    row.classList.add("is-editing")

    if (activeInput) {
      activeInput.focus()
      if (activeInput.tagName === "TEXTAREA") {
        this._fitTextarea(activeInput)
        activeInput.selectionStart = activeInput.selectionEnd = activeInput.value.length
      } else {
        activeInput.select()
      }
    }
  }

  autoResize(event) {
    this._fitTextarea(event.currentTarget)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      const row = event.currentTarget.closest("[data-field-path]")
      if (row) this._closeEditor(row)
    } else if (event.key === "Enter") {
      const isTextarea = event.currentTarget.tagName === "TEXTAREA"
      if (isTextarea) {
        // Save on ⌘+Enter or Ctrl+Enter; plain Enter adds a newline
        if (event.metaKey || event.ctrlKey) {
          event.preventDefault()
          this.confirm(event)
        }
      } else if (!event.shiftKey) {
        event.preventDefault()
        this.confirm(event)
      }
    }
  }

  handleBlur(event) {
    const row = event.currentTarget.closest("[data-field-path]")
    if (!row) return

    setTimeout(() => {
      if (row.contains(document.activeElement)) return
      const input = row.querySelector("[data-role='editor'] input:not(.hidden), [data-role='editor'] textarea:not(.hidden)")
      if (!input) return

      // If value unchanged, just close
      if (input.value === input.dataset.originalValue) {
        this._closeEditor(row)
      }
      // If changed, leave open so user must click save or press Enter
    }, 150)
  }

  async confirm(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("[data-field-path]")
    if (!row) return
    const fromReview = row.classList.contains("pdf-review-row--editing")
    if (this.reviewModeActive() && !fromReview) return

    const fieldPath = row.dataset.fieldPath
    const input = row.querySelector("[data-role='editor'] input:not(.hidden), [data-role='editor'] textarea:not(.hidden)")
    if (!input) return

    const newValue = input.value
    const display = row.querySelector("[data-role='display']")
    const indicator = row.querySelector("[data-role='indicator']")

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ field_path: fieldPath, value: newValue })
      })

      const data = await response.json()

      if (data.success) {
        display.textContent = newValue
        row.dataset.rawValue = newValue
        this._formatRow(row)
        this._closeEditor(row)
        this._syncSummaryProjection(fieldPath, newValue)
        row.dispatchEvent(new CustomEvent("extracted-data-editor:field-updated", {
          bubbles: true,
          detail: { fieldPath, value: newValue }
        }))
        this._flashIndicator(indicator, "success")
        row.classList.add("xd-row--saved")
        setTimeout(() => row.classList.remove("xd-row--saved"), 1500)
      } else {
        this._flashIndicator(indicator, "error")
      }
    } catch (error) {
      console.error("Failed to save:", error)
      this._flashIndicator(indicator, "error")
    }
  }

  async deleteNode(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.reviewModeActive()) return

    // event.currentTarget is null once the listener yields, so capture now.
    const trigger = event.currentTarget
    const nodePath = trigger.dataset.nodePath
    if (!nodePath) return

    const pathLabel = nodePath.split(".").map(p => p.match(/^\d+$/) ? `#${parseInt(p) + 1}` : p.replace(/_/g, " ")).join(" › ")
    if (!confirm(`Remove "${pathLabel}"? This cannot be undone.`)) return

    try {
      const response = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ field_path: nodePath })
      })

      const data = await response.json()

      if (data.success) {
        // Find and remove the DOM element. Check item rows first: their
        // closest("details") is the surrounding section, not the item.
        const itemRow = trigger.closest("[data-role='item-row']")
        if (itemRow) {
          const detailRow = this._detailRowFor(itemRow)
          const table = itemRow.closest("table[data-array-path]")
          itemRow.style.transition = "opacity 0.2s"
          itemRow.style.opacity = "0"
          setTimeout(() => { detailRow?.remove(); itemRow.remove() }, 200)
          this._removeCachedArrayItem(nodePath)
          this._refreshTableFooter(table)
          return
        }
        const details = trigger.closest("details")
        const row = trigger.closest("[data-field-path]")
        const el = details || row
        if (el) {
          el.style.transition = "opacity 0.2s, max-height 0.3s"
          el.style.opacity = "0"
          el.style.maxHeight = el.offsetHeight + "px"
          el.style.overflow = "hidden"
          setTimeout(() => {
            el.style.maxHeight = "0"
            setTimeout(() => el.remove(), 300)
          }, 100)
        }
      } else {
        alert("Failed to delete: " + (data.error || "Unknown error"))
      }
    } catch (error) {
      console.error("Failed to delete:", error)
      alert("Failed to delete. Please try again.")
    }
  }

  // --- Lazy materialization of large arrays ---
  // Arrays beyond the server-side threshold render only their first items plus
  // a placeholder; the rest are built here by cloning the <template> elements
  // (same partials as SSR) and filling them from the embedded JSON. All values
  // are written with textContent/setAttribute — extracted data is untrusted.

  showAllItems(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.closest("[data-array-path]")
    if (placeholder) this._materializePlaceholder(placeholder)
  }

  // Review matching needs every field row in the DOM, so this also builds the
  // detail rows that normal viewing leaves deferred.
  materializeAll() {
    this._materializeWithin(this.element)
    this.element.querySelectorAll("[data-detail-pending]").forEach(detail => this._hydrateDetail(detail))
  }

  _materializeWithin(root) {
    // Re-query each pass: materializing can reveal nothing new (client render
    // is always full-depth), but stay defensive against nested placeholders.
    let guard = 0
    let placeholder
    while ((placeholder = root.querySelector("[data-extracted-data-editor-target='lazyRest']")) && guard < 100) {
      guard++
      this._materializePlaceholder(placeholder)
    }
  }

  _materializePlaceholder(placeholder) {
    const path = placeholder.dataset.arrayPath
    const parentKey = placeholder.dataset.parentKey || ""
    const depth = parseInt(placeholder.dataset.depth, 10) || 0
    const start = parseInt(placeholder.dataset.startIndex, 10) || 0
    let itemSchema = {}
    try { itemSchema = JSON.parse(placeholder.dataset.itemSchema || "{}") } catch { /* schema optional */ }

    const array = this._valueAtPath(path)
    if (!Array.isArray(array)) {
      placeholder.remove()
      return
    }

    // Table-mode placeholders (arrays of hashes) embed their summary columns
    // so client-built rows project the same fields as the server-rendered ones.
    let columns = null
    if (placeholder.dataset.summaryColumns) {
      try { columns = JSON.parse(placeholder.dataset.summaryColumns) } catch { columns = null }
    }

    const fragment = document.createDocumentFragment()
    for (let i = start; i < array.length; i++) {
      if (columns) {
        const [row, detail] = this._buildTableRows(array[i], i, `${path}.${i}`, depth, itemSchema, columns)
        fragment.appendChild(row)
        fragment.appendChild(detail)
      } else {
        const node = this._buildArrayEntry(array[i], i, `${path}.${i}`, parentKey, depth, itemSchema)
        if (node) fragment.appendChild(node)
      }
    }
    placeholder.before(fragment)
    placeholder.remove()
  }

  // Builds a summary row + hidden detail row pair for one array-table item.
  _buildTableRows(item, index, itemPath, depth, itemSchema, columns) {
    const row = this.arrayRowTemplateTarget.content.firstElementChild.cloneNode(true)
    row.dataset.itemPath = itemPath
    const indexCell = row.querySelector("td")
    if (indexCell) indexCell.style.paddingLeft = this._padPx(depth)
    this._setText(row, "[data-role='item-index']", String(index + 1))
    const actions = row.querySelector("[data-role='item-actions']")
    const deleteBtn = actions?.querySelector("[data-node-path]")
    if (deleteBtn) deleteBtn.dataset.nodePath = itemPath
    for (const col of columns) {
      const cell = this._buildSummaryCell(col, this._isPlainObject(item) ? item[col.key] : null, itemPath)
      row.insertBefore(cell, actions)
    }

    // The detail stays empty and pending, like its server-rendered twin; it is
    // filled by _hydrateDetail the first time the row is opened.
    const detail = this.arrayDetailTemplateTarget.content.firstElementChild.cloneNode(true)
    detail.querySelector("td").colSpan = columns.length + 2
    return [row, detail]
  }

  _buildSummaryCell(col, value, itemPath) {
    const cell = document.createElement("td")
    cell.className = col.numeric ? SUMMARY_CELL_NUMERIC_CLASS : SUMMARY_CELL_CLASS
    cell.dataset.summaryPath = `${itemPath}.${col.key}`
    cell.dataset.summaryCol = col.key
    cell.dataset.summaryKind = col.numeric ? "numeric" : "text"
    if (col.identifier) cell.dataset.summaryRole = "identifier"
    else cell.dataset.summaryLabel = col.label
    this._fillSummaryCell(cell, value)
    return cell
  }

  _fillSummaryCell(cell, value) {
    const str = value == null ? "" : String(value)
    const nodes = []

    // Rebuilt from the data attribute so saves don't strip the card-mode label.
    if (cell.dataset.summaryLabel) {
      const label = document.createElement("span")
      label.dataset.role = "summary-label"
      label.textContent = cell.dataset.summaryLabel
      nodes.push(label)
    }

    if (str === "") {
      cell.title = ""
      const dash = document.createElement("span")
      dash.className = "text-ink-faint/60"
      dash.textContent = "–"
      nodes.push(dash)
    } else {
      const shown = displayFor(cell.dataset.summaryCol, str)
      cell.title = str
      cell.classList.toggle("xd-v--mono", shown.mono)
      nodes.push(document.createTextNode(shown.text))
    }
    cell.replaceChildren(...nodes)
  }

  // ---- Readable values (lib/value_format.js) ---------------------------------
  // Amounts gain separators and two decimals, counts gain separators, and
  // identifiers are set in mono. The stored value stays on the row
  // (data-raw-value) for the editor, the filter and the copy button, which
  // copies the plain form ("1180.00").

  _formatValues(root) {
    root.querySelectorAll("tr[data-field-path]").forEach(row => this._formatRow(row))
    root.querySelectorAll("td[data-summary-path]").forEach(cell => {
      if (cell.title) this._fillSummaryCell(cell, cell.title)
    })
    root.querySelectorAll("td[data-sum-col]").forEach(cell => {
      const text = (cell.querySelector("[data-role='footer-value']") || cell).textContent.replace(/,/g, "").trim()
      if (text !== "" && !Number.isNaN(Number(text))) this._writeSum(cell, Number(text))
    })
  }

  _formatRow(row) {
    if (row.querySelector("[data-role='empty-placeholder']:not(.hidden)")) return
    const targets = row.querySelectorAll("[data-role='display'], [data-role='value-text']")
    if (targets.length === 0) return
    if (row.dataset.rawValue === undefined) row.dataset.rawValue = targets[0].textContent.trim()
    const raw = row.dataset.rawValue
    const copyButton = row.querySelector(".xd-copy")
    if (copyButton) copyButton.hidden = raw === ""

    const shown = displayFor(leafKey(row.dataset.fieldPath), raw)
    targets.forEach(target => {
      if (target.textContent !== shown.text) target.textContent = shown.text
      target.classList.toggle("xd-v--mono", shown.mono)
    })
    const copyHost = row.querySelector("[data-role='value-cell'][data-controller~='clipboard']")
    if (copyHost) copyHost.dataset.clipboardTextValue = shown.copy
  }

  // Builds a full summary table for a nested array of hashes inside lazily
  // materialized content. Footer sums are skipped here (rare path).
  _buildArrayTable(array, path, depth, itemSchema) {
    const table = this.arrayTableTemplateTarget.content.firstElementChild.cloneNode(true)
    table.dataset.arrayPath = path
    // Deferred detail rows read both back off the table they sit in.
    table.dataset.depth = String(depth)
    table.dataset.itemSchema = JSON.stringify(itemSchema || {})
    const columns = this._summaryColumns(array, itemSchema)

    const headRow = table.querySelector("thead tr")
    const actionsHead = headRow.querySelector("[data-role='actions-head']")
    const indexHead = headRow.querySelector("th")
    indexHead.style.paddingLeft = this._padPx(depth)
    indexHead.style.width = (Math.min(depth * 16 + 12, 64) + 44) + "px"
    for (const col of columns) {
      const th = document.createElement("th")
      th.className = col.numeric ? SUMMARY_HEADER_NUMERIC_CLASS : SUMMARY_HEADER_CLASS
      th.textContent = col.label
      th.title = col.label
      if (col.numeric) th.style.width = `${SUMMARY_NUMERIC_WIDTH_PX}px`
      headRow.insertBefore(th, actionsHead)
    }

    const autoExpand = array.length <= AUTO_EXPAND_LIMIT
    const tbody = table.querySelector("[data-role='array-body']")
    array.forEach((item, i) => {
      const [row, detail] = this._buildTableRows(item, i, `${path}.${i}`, depth, itemSchema, columns)
      if (autoExpand) this._setItemRowExpanded(row, detail, true)
      tbody.appendChild(row)
      tbody.appendChild(detail)
    })
    return table
  }

  // Mirrors PaperworksHelper#extracted_data_column_rank.
  _columnRank(key, numeric, varied = true) {
    const normalized = String(key).toLowerCase()
    if (INDEX_KEY_PATTERN.test(normalized)) return 4
    if (!numeric && !varied) return 3
    if (!numeric && DATE_KEY_PATTERN.test(normalized)) return 3
    if (!numeric && LABEL_KEY_PATTERN.test(normalized)) return 0
    if (numeric && TOTAL_KEY_PATTERN.test(normalized)) return 1
    return numeric ? 3 : 2
  }

  // Mirrors PaperworksHelper#extracted_data_identifying_distinct_count.
  _identifyingDistinctCount(rowCount) {
    return Math.max(2, Math.ceil(rowCount * 0.1))
  }

  // Mirrors PaperworksHelper#extracted_data_summary_columns.
  _summaryColumns(items, schemaProps) {
    const first = items.find(item => this._isPlainObject(item))
    if (!first) return []

    const distinctNeeded = this._identifyingDistinctCount(items.length)

    const candidates = []
    let position = -1
    for (const [key, value] of this._orderedEntries(first, schemaProps)) {
      position++
      if (this._isPlainObject(value) || Array.isArray(value)) continue
      if (FIELD_LABEL_KEY_PATTERN.test(String(key).toLowerCase())) continue
      const type = String(schemaProps?.[key]?.type || "")
      if (type === "object" || type === "array") continue

      let sample = null
      const distinct = new Set()
      for (const item of items) {
        const candidate = this._isPlainObject(item) ? item[key] : null
        if (candidate == null || candidate === "") continue
        if (sample === null) sample = candidate
        distinct.add(candidate)
        if (distinct.size >= distinctNeeded) break
      }
      const numeric = type === "number" || type === "integer" || typeof sample === "number"
      const varied = items.length <= 1 || distinct.size >= distinctNeeded
      candidates.push({ key, label: this._titleize(key), numeric, position, varied })
    }

    const ranked = candidates
      .sort((a, b) => (this._columnRank(a.key, a.numeric, a.varied) - this._columnRank(b.key, b.numeric, b.varied)) || (a.position - b.position))
      .slice(0, SUMMARY_COLUMN_LIMIT)

    // Best-ranked text column, not the leftmost one — a description identifies
    // a row better than the part code that happens to precede it.
    const identifier = ranked.find(col => !col.numeric)

    return ranked
      .sort((a, b) => a.position - b.position)
      .map(({ key, label, numeric }) => ({ key, label, numeric, identifier: key === identifier?.key }))
  }

  _buildArrayEntry(item, index, itemPath, parentKey, depth, itemSchema) {
    if (this._isPlainObject(item)) {
      const details = this.arrayItemTemplateTarget.content.firstElementChild.cloneNode(true)
      const summary = details.querySelector("summary")
      summary.style.paddingLeft = this._padPx(depth)
      this._setText(summary, "[data-role='item-index']", String(index + 1))
      this._setText(summary, "[data-role='item-label']", this._singularTitle(parentKey))
      this._setText(summary, "[data-role='item-preview']", this._truncate(String(Object.values(item)[0] ?? ""), 30))
      const deleteBtn = summary.querySelector("[data-node-path]")
      if (deleteBtn) deleteBtn.dataset.nodePath = itemPath
      details.appendChild(this._buildRowsTable(item, itemPath, depth + 1, itemSchema))
      return details
    }

    const row = this.fieldRowTemplateTarget.content.firstElementChild.cloneNode(true)
    this._fillFieldRow(row, {
      path: itemPath,
      label: `${this._singularTitle(parentKey)} ${index + 1}`,
      value: item,
      depth: depth,
      schema: {}
    })
    return this._wrapFieldRow(row)
  }

  _buildRowsTable(obj, basePath, depth, schemaProps) {
    const table = document.createElement("table")
    table.className = "w-full text-sm table-fixed"
    const tbody = document.createElement("tbody")
    table.appendChild(tbody)

    for (const [key, value] of this._orderedEntries(obj, schemaProps)) {
      tbody.appendChild(this._buildNodeRow(key, value, `${basePath}.${key}`, depth, schemaProps?.[key] || {}))
    }
    return table
  }

  _buildNodeRow(key, value, path, depth, fieldSchema) {
    if (this._isPlainObject(value) || Array.isArray(value)) {
      const isArray = Array.isArray(value)
      const sectionRow = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
      const details = sectionRow.querySelector("details")
      const summary = details.querySelector("summary")
      summary.style.paddingLeft = this._padPx(depth)
      this._setText(summary, "[data-role='section-title']", this._titleize(key))
      this._setText(summary, "[data-role='section-count']", String(isArray ? value.length : Object.keys(value).length))

      const deleteBtn = summary.querySelector("[data-node-path]")
      if (isArray) {
        deleteBtn?.remove() // SSR array sections have no delete control
      } else if (deleteBtn) {
        deleteBtn.dataset.nodePath = path
      }

      if (isArray) {
        const itemSchema = fieldSchema?.items?.properties || {}
        if (value.length > 0 && value.every(item => this._isPlainObject(item))) {
          details.appendChild(this._buildArrayTable(value, path, depth + 1, itemSchema))
        } else {
          value.forEach((item, i) => {
            const node = this._buildArrayEntry(item, i, `${path}.${i}`, key, depth + 1, itemSchema)
            if (node) details.appendChild(node)
          })
        }
      } else {
        details.appendChild(this._buildRowsTable(value, path, depth + 1, fieldSchema?.properties || {}))
      }
      return sectionRow
    }

    const row = this.fieldRowTemplateTarget.content.firstElementChild.cloneNode(true)
    this._fillFieldRow(row, {
      path: path,
      label: this._titleize(key),
      value: value,
      depth: depth,
      schema: fieldSchema || {}
    })
    return row
  }

  _wrapFieldRow(row) {
    const table = document.createElement("table")
    table.className = "w-full text-sm table-fixed"
    const tbody = document.createElement("tbody")
    tbody.appendChild(row)
    table.appendChild(tbody)
    return table
  }

  _fillFieldRow(row, { path, label, value, depth, schema }) {
    const valueStr = value == null ? "" : String(value)
    const schemaDesc = typeof schema?.description === "string" ? schema.description : ""
    const schemaType = String(schema?.type || "")
    const isDate = schemaType === "date" || /format\s+YYYY-MM-DD/i.test(schemaDesc)
    const isNumber = schemaType === "number" || schemaType === "integer"

    row.dataset.fieldPath = path
    row.querySelectorAll(".pdf-review-slot [data-field-path]").forEach(el => { el.dataset.fieldPath = path })

    const labelCell = row.querySelector("[data-role='label']")
    if (labelCell) labelCell.style.paddingLeft = this._padPx(depth)
    const labelText = row.querySelector("[data-role='label-text']")
    if (labelText) {
      labelText.textContent = label
      labelText.setAttribute("title", schemaDesc || path)
    }
    const copyButton = row.querySelector(".xd-copy")
    if (copyButton) {
      copyButton.setAttribute("title", `Copy ${label}`)
      copyButton.setAttribute("aria-label", `Copy ${label}`)
    }
    const desc = row.querySelector("[data-role='desc']")
    if (desc && schemaDesc) {
      desc.textContent = schemaDesc
      desc.classList.remove("hidden")
    }

    // Editor mode: fill display span + input
    const display = row.querySelector("[data-role='display']")
    if (display) {
      if (valueStr !== "") display.textContent = valueStr
      display.setAttribute("title", valueStr)
      const input = row.querySelector("[data-role='editor'] input")
      if (input) {
        if (isDate || isNumber) {
          input.type = isDate ? "date" : "number"
          if (isNumber) input.setAttribute("step", "any")
        }
        input.value = isDate ? this._normalizeDate(valueStr) : valueStr
      }
      const textarea = row.querySelector("[data-role='editor'] textarea")
      if (textarea) textarea.value = valueStr
    }

    // Readonly mode: fill expandable value, toggle empty placeholder
    const expandWrap = row.querySelector("[data-role='expand-wrap']")
    if (expandWrap && valueStr !== "") {
      expandWrap.classList.remove("hidden")
      row.querySelector("[data-role='empty-placeholder']")?.classList.add("hidden")
      row.classList.add("cursor-pointer")
      row.querySelector("[data-value-expand-target='collapsed']")?.setAttribute("title", valueStr)
      row.querySelectorAll("[data-role='value-text']").forEach(el => { el.textContent = valueStr })
    }
    row.dataset.rawValue = valueStr
    this._formatRow(row)
  }

  _orderedEntries(obj, schemaProps) {
    const schemaKeys = Object.keys(schemaProps || {})
    const order = new Map(schemaKeys.map((k, i) => [k, i]))
    return Object.entries(obj).sort(([a], [b]) => {
      const ai = order.has(a) ? order.get(a) : schemaKeys.length
      const bi = order.has(b) ? order.get(b) : schemaKeys.length
      if (ai !== bi) return ai - bi
      const aMissing = order.has(a) ? 0 : 1
      const bMissing = order.has(b) ? 0 : 1
      if (aMissing !== bMissing) return aMissing - bMissing
      return a < b ? -1 : a > b ? 1 : 0
    })
  }

  _valueAtPath(path) {
    if (!path) return this._parsedJson()
    return path.split(".").reduce((acc, segment) => {
      if (acc == null) return undefined
      return Array.isArray(acc) ? acc[Number(segment)] : acc[segment]
    }, this._parsedJson())
  }

  // --- Summary-table projections (cells + footer sums) ---
  // Summary cells are display-only copies of fields edited in detail rows, so
  // saves and deletes update the JSON cache and re-derive cells/sums from it.

  _syncSummaryProjection(path, value) {
    this._setCachedValue(path, value)
    const cell = this.element.querySelector(`[data-summary-path="${CSS.escape(path)}"]`)
    if (cell) this._fillSummaryCell(cell, value)
    this._refreshSumFor(path)
  }

  _setCachedValue(path, value) {
    const json = this._parsedJson()
    if (!json || !path) return
    const segments = path.split(".")
    const last = segments.pop()
    const parent = segments.reduce((acc, segment) => {
      if (acc == null) return undefined
      return Array.isArray(acc) ? acc[Number(segment)] : acc[segment]
    }, json)
    if (parent == null || typeof parent !== "object") return
    parent[Array.isArray(parent) ? Number(last) : last] = value
  }

  _removeCachedArrayItem(path) {
    const segments = path.split(".")
    const index = Number(segments.pop())
    const array = this._valueAtPath(segments.join("."))
    if (Array.isArray(array) && Number.isInteger(index)) array.splice(index, 1)
  }

  _refreshSumFor(fieldPath) {
    const segments = fieldPath.split(".")
    if (segments.length < 3) return
    const col = segments.pop()
    segments.pop()
    const arrayPath = segments.join(".")
    const table = this.element.querySelector(`table[data-array-path="${CSS.escape(arrayPath)}"]`)
    const sumCell = table?.querySelector(`[data-sum-col="${CSS.escape(col)}"]`)
    if (!sumCell) return
    const array = this._valueAtPath(arrayPath)
    if (!Array.isArray(array)) return
    this._writeSum(sumCell, this._sumColumn(array, col))
  }

  _refreshTableFooter(table) {
    if (!table) return
    const array = this._valueAtPath(table.dataset.arrayPath)
    if (!Array.isArray(array)) return
    const countCell = table.querySelector("[data-role='array-count']")
    if (countCell) {
      const noun = array.length === 1 ? countCell.dataset.countOne : countCell.dataset.countOther
      countCell.textContent = `${array.length} ${noun || "items"}`
    }
    table.querySelectorAll("[data-sum-col]").forEach(cell => {
      this._writeSum(cell, this._sumColumn(array, cell.dataset.sumCol))
    })
  }

  // Mirrors PaperworksHelper#extracted_data_column_sum.
  _sumColumn(array, key) {
    let sum = 0
    let found = false
    for (const item of array) {
      if (!this._isPlainObject(item)) continue
      const raw = item[key]
      if (raw == null || raw === "") continue
      const num = typeof raw === "number" ? raw : Number(raw)
      if (Number.isFinite(num)) { sum += num; found = true }
    }
    return found ? sum : null
  }

  // Mirrors PaperworksHelper#extracted_data_format_sum.
  // A column total reads like the column: an amount column's total has two
  // decimals ("1,053.00"). Writes into the value span, so the narrow layout's
  // footer label survives.
  _writeSum(cell, sum) {
    const target = cell.querySelector("[data-role='footer-value']") || cell
    target.textContent = this._formatSum(sum, cell.dataset.sumCol)
  }

  _formatSum(sum, col) {
    if (sum == null) return ""
    const rounded = Math.round(sum * 100) / 100
    if (col) {
      const shown = displayFor(col, String(rounded))
      if (shown.numeric) return shown.text
    }
    return rounded.toLocaleString("en-US", {
      minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
      maximumFractionDigits: 2
    })
  }

  // Opens collapsed sections/detail rows above a field row so it is visible.
  _revealFieldRow(row) {
    let el = row.parentElement
    while (el && el !== this.element) {
      if (el.tagName === "DETAILS") el.open = true
      if (el.matches("[data-role='item-detail']") && el.classList.contains("hidden")) {
        el.classList.remove("hidden")
        el.previousElementSibling?.setAttribute("aria-expanded", "true")
      }
      el = el.parentElement
    }
  }

  _parsedJson() {
    if (this._jsonCache === undefined) {
      try { this._jsonCache = JSON.parse(this.jsonValue) } catch { this._jsonCache = null }
    }
    return this._jsonCache
  }

  _isPlainObject(value) {
    return value != null && typeof value === "object" && !Array.isArray(value)
  }

  _setText(scope, selector, text) {
    const el = scope.querySelector(selector)
    if (el) el.textContent = text
  }

  _padPx(depth) {
    return Math.min(depth * 16 + 12, 64) + "px"
  }

  _truncate(text, length) {
    return text.length > length ? text.slice(0, length - 3) + "..." : text
  }

  // Sentence-case label for a key (lib/field_label.js).
  _titleize(key) {
    return fieldLabel(key)
  }

  _singularTitle(key) {
    let word = String(key)
    if (/ies$/i.test(word)) word = word.replace(/ies$/i, "y")
    else if (/sses$/i.test(word)) word = word.replace(/es$/i, "")
    else if (/s$/i.test(word) && !/ss$/i.test(word)) word = word.replace(/s$/i, "")
    return this._titleize(word)
  }

  _normalizeDate(value) {
    if (!value) return value
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
    const parsed = new Date(value)
    if (isNaN(parsed)) return value
    return parsed.toISOString().slice(0, 10)
  }

  _closeEditor(row) {
    const display = row.querySelector("[data-role='display']")
    const editor = row.querySelector("[data-role='editor']")
    if (display) display.classList.remove("hidden")
    row.classList.remove("pdf-review-row--editing")
    if (editor) {
      editor.classList.add("hidden")
      // Reset textarea/input visibility for next edit
      const textarea = editor.querySelector("textarea")
      const input = editor.querySelector("input")
      if (textarea) textarea.classList.add("hidden")
      if (input) input.classList.remove("hidden")
      editor.classList.replace("items-start", "items-center")
    }

    row.classList.remove("is-editing")
  }

  _fitTextarea(textarea) {
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
  }

  _flashIndicator(indicator, type) {
    if (!indicator) return

    if (type === "success") {
      indicator.innerHTML = `<i class="ti ti-circle-check-filled text-lg text-success-dark"></i>`
      indicator.style.animation = "none"
      indicator.offsetHeight // trigger reflow
      indicator.style.animation = "saved-pop 0.5s ease-out"
    } else {
      indicator.innerHTML = `<i class="ti ti-exclamation-mark text-lg text-error"></i>`
    }
    indicator.classList.remove("hidden")
    setTimeout(() => {
      indicator.classList.add("hidden")
      indicator.innerHTML = ""
      indicator.style.animation = ""
    }, 2000)
  }

  reviewModeActive() {
    return this.reviewModeValue || Boolean(this.element.closest(".pdf-review-active"))
  }

  setJsonControlsEnabled(enabled) {
    if (this.hasJsonTabTarget) {
      this.jsonTabTarget.disabled = !enabled
      this.jsonTabTarget.classList.toggle("hidden", !enabled)
      this.jsonTabTarget.setAttribute("aria-disabled", enabled ? "false" : "true")
    }
    if (this.hasCopyBtnTarget) {
      this.copyBtnTarget.disabled = !enabled
      this.copyBtnTarget.classList.toggle("hidden", !enabled)
    }
  }

  flashReviewModeNotice() {
    if (!this.hasFormTabTarget) return

    const originalTitle = this.formTabTarget.title
    this.formTabTarget.title = "Review mode uses form view so fields can be matched, edited, and approved."
    this.formTabTarget.classList.add("animate-pulse")
    setTimeout(() => {
      this.formTabTarget.classList.remove("animate-pulse")
      this.formTabTarget.title = originalTitle
    }, 1400)
  }
}
